/**
 * Analytics API Module
 * API calls for native analytics data
 */

import apiClient from '../core';

// ============================================================================
// Types
// ============================================================================

export interface AnalyticsOverview {
  pageViews: number;
  sessions: number;
  avgPagesPerSession: number | null;
  bounceRate: number | null;
  avgSessionDuration: number | null; // Average session duration in seconds
  previousPageViews?: number;
  previousSessions?: number;
  previousAvgPagesPerSession?: number | null;
  previousBounceRate?: number | null;
  previousAvgSessionDuration?: number | null;
}

export interface AnalyticsTimelinePoint {
  date: string;
  pageViews: number;
  sessions: number;
}

export interface AnalyticsPageStats {
  pagePath: string;
  pageViews: number;
  entrances: number;
  exits: number;
  exitRate: number | null;
  bounces: number;
  bounceRate: number | null;
}

export interface AnalyticsPathStats {
  fromPagePath: string;
  toPagePath: string;
  transitions: number;
  percentage?: number;
}

export interface AnalyticsReferrerStats {
  referrerHost: string;
  hits: number;
}

export interface AnalyticsDeviceStats {
  deviceType: string;
  hits: number;
}

export interface AnalyticsBrowserStats {
  browser: string;
  hits: number;
}

export interface AnalyticsScreenStats {
  category: string;
  hits: number;
}

export interface AnalyticsLandingPageStats {
  pagePath: string;
  entrances: number;
  bounceRate: number | null;
}

export interface AnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface SankeyNode {
  id: string;
  label: string;
  title?: string;
  pageViews?: number;
  bounceRate?: number | null;
}

export interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
  hasMore?: boolean;
}

export interface SankeyOptions {
  startPage?: string;
  maxDepth?: number;
  minTransitions?: number;
  minPct?: number;
  showExits?: boolean;
  expanded?: boolean;
}

/** node-pg sends COUNT(*) as a string; coerce so charts can add hits. */
export function normalizeHits<T extends { hits: number | string }>(
  rows: T[]
): Array<T & { hits: number }> {
  return rows.map(row => {
    const n = Number(row.hits);
    return { ...row, hits: Number.isFinite(n) ? n : 0 };
  });
}

// ============================================================================
// API Functions
// ============================================================================

export const analyticsApi = {
  /**
   * Get analytics overview (KPIs) for a site
   */
  async getOverview(siteId: string, dateRange?: AnalyticsDateRange): Promise<AnalyticsOverview> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);

    const queryString = params.toString();
    const url = `/analytics/${siteId}/overview${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsOverview>(url);
    return response.data;
  },

  /**
   * Get timeline data (page views per day)
   */
  async getTimeline(
    siteId: string,
    dateRange?: AnalyticsDateRange
  ): Promise<AnalyticsTimelinePoint[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);

    const queryString = params.toString();
    const url = `/analytics/${siteId}/timeline${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsTimelinePoint[]>(url);
    return response.data;
  },

  /**
   * Get top pages by views
   */
  async getPages(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    limit?: number
  ): Promise<AnalyticsPageStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (limit) params.set('limit', String(limit));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/pages${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsPageStats[]>(url);
    return response.data;
  },

  /**
   * Get navigation paths
   */
  async getPaths(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    limit?: number
  ): Promise<AnalyticsPathStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (limit) params.set('limit', String(limit));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/paths${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsPathStats[]>(url);
    return response.data;
  },

  /**
   * Get referrer breakdown
   */
  async getReferrers(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    limit?: number
  ): Promise<AnalyticsReferrerStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (limit) params.set('limit', String(limit));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/referrers${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsReferrerStats[]>(url);
    return normalizeHits(response.data ?? []);
  },

  /**
   * Get device breakdown
   */
  async getDevices(
    siteId: string,
    dateRange?: AnalyticsDateRange
  ): Promise<AnalyticsDeviceStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);

    const queryString = params.toString();
    const url = `/analytics/${siteId}/devices${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsDeviceStats[]>(url);
    return normalizeHits(response.data ?? []);
  },

  /**
   * Get Sankey diagram data for navigation flow visualization
   */
  async getSankey(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    options?: SankeyOptions
  ): Promise<SankeyData> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (options?.startPage) params.set('startPage', options.startPage);
    if (options?.maxDepth !== undefined) params.set('maxDepth', String(options.maxDepth));
    if (options?.minTransitions !== undefined)
      params.set('minTransitions', String(options.minTransitions));
    if (options?.minPct !== undefined) params.set('minPct', String(options.minPct));
    if (options?.showExits !== undefined) params.set('showExits', String(options.showExits));
    if (options?.expanded !== undefined) params.set('expanded', String(options.expanded));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/paths/sankey${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<SankeyData>(url);
    return response.data;
  },

  /**
   * Get browser breakdown
   */
  async getBrowsers(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    limit?: number
  ): Promise<AnalyticsBrowserStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (limit) params.set('limit', String(limit));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/browsers${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsBrowserStats[]>(url);
    return normalizeHits(response.data ?? []);
  },

  /**
   * Get screen size breakdown
   */
  async getScreens(
    siteId: string,
    dateRange?: AnalyticsDateRange
  ): Promise<AnalyticsScreenStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);

    const queryString = params.toString();
    const url = `/analytics/${siteId}/screens${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsScreenStats[]>(url);
    return normalizeHits(response.data ?? []);
  },

  /**
   * Get top landing pages (by entrances)
   */
  async getLandingPages(
    siteId: string,
    dateRange?: AnalyticsDateRange,
    limit?: number
  ): Promise<AnalyticsLandingPageStats[]> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (limit) params.set('limit', String(limit));

    const queryString = params.toString();
    const url = `/analytics/${siteId}/landing-pages${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<AnalyticsLandingPageStats[]>(url);
    return response.data;
  },
};

export default analyticsApi;
